import { gql, useQuery } from '@apollo/react-hooks';
import type {
  GetAcademicQualificationData,
  AcademicQualificationData,
} from './types';
import {
  awardClassificationOptions,
  levelOfDegreeOptions,
} from '../../../../../Application/components';
import { getOptionsLabel } from '../../../../../Monitor/components';
import { type Mark } from '../../../../../Application/graphql';
import type { SelectOption, FileData } from '../../../../../../components';

export const useGetAcademicQualificationData = (
  id: string,
): GetAcademicQualificationData => {
  const { data, loading, error } = useQuery(
    gql`
      query ($id: ID!) {
        programmeChoiceMetadata {
          countryList {
            value
            label
          }
        }

        academicQualification(id: $id) {
          degreeLevel
          institution {
            name
            country
          }
          marks {
            course
            credit
            result
          }
          convertGPARuleId
          markConversion {
            convertedMark
            digitToLetterRule {
              name
            }
          }
          gpaIndicator
          rankIndicator
          officialMark
          outOfGpa
          officialClassRanking
          outOfRank
          awardClassification
          otherDocuments {
            id
            link
            name
          }
          markExplanations {
            id
            link
            name
          }
          classRankProofs {
            id
            link
            name
          }
          certificates {
            id
            link
            name
          }
          transcripts {
            id
            link
            name
          }
        }
      }
    `,
    {
      variables: { id },
      skip: !id,
      fetchPolicy: 'network-only',
    },
  );

  const rawData = data?.academicQualification;
  const countryList: SelectOption[] =
    data?.programmeChoiceMetadata?.countryList ?? [];
  const academicQualificationData: AcademicQualificationData = {
    degreeLevel: getOptionsLabel(levelOfDegreeOptions, rawData?.degreeLevel),
    institutionName: rawData?.institution?.name,
    countryRegion: getOptionsLabel(countryList, rawData?.institution?.country),
    convertedMark: (rawData?.markConversion?.convertedMark as boolean)
      ? rawData?.markConversion?.convertedMark
      : 'N/A',
    digitToLetterRule: rawData?.markConversion?.digitToLetterRule?.name ?? '',
    officialClassRanking: (rawData?.officialClassRanking as boolean)
      ? rawData?.officialClassRanking
      : '0',
    rankIndicator: rawData?.rankIndicator as boolean,
    gpaIndicator: rawData?.gpaIndicator as boolean,
    officialMark: (rawData?.officialMark as boolean)
      ? rawData?.officialMark
      : '0',
    outOfGpa: (rawData?.outOfGpa as boolean) ? rawData?.outOfGpa : '0',
    outOfRank: (rawData?.outOfRank as boolean) ? rawData?.outOfRank : '0',
    awardClassification: getOptionsLabel(
      awardClassificationOptions,
      rawData?.awardClassification,
    ),

    marks: (rawData?.marks as Mark[]) ?? [],
    convertGPARuleId: rawData?.convertGPARuleId ?? '',

    transcripts: (rawData?.transcripts as FileData[]) ?? [],
    otherDocuments: (rawData?.otherDocuments as FileData[]) ?? [],
    markExplanations: (rawData?.markExplanations as FileData[]) ?? [],
    classRankProofs: (rawData?.classRankProofs as FileData[]) ?? [],
    certificates: (rawData?.certificates as FileData[]) ?? [],
  };

  return { academicQualificationData, loading, error };
};
